import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import PageTitle from '../shared/PageTitle';

const Subscriptions = () => {
  const [subscriptions, setSubscriptions] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewSubscriptionModal, setShowNewSubscriptionModal] = useState(false);
  const [formData, setFormData] = useState({
    tenant_id: '',
    plan_id: '',
    start_date: new Date().toISOString().split('T')[0],
    end_date: '',
    payment_method: 'manual'
  });
  const [formErrors, setFormErrors] = useState({});
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      // Use relative path in production, localhost in development
      const apiBaseURL = process.env.REACT_APP_API_URL ||
        (window.location.hostname === 'localhost' ? 'https://nivaas.app' : '');

      console.log('Fetching subscription data...');

      const [subsResponse, tenantsResponse, plansResponse] = await Promise.all([
        axios.get('/api/saas/subscriptions', {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
          ...(apiBaseURL && { baseURL: apiBaseURL })
        }),
        axios.get('/api/saas/tenants', {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
          ...(apiBaseURL && { baseURL: apiBaseURL })
        }),
        axios.get('/api/saas/subscriptions/plans', {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
          ...(apiBaseURL && { baseURL: apiBaseURL })
        })
      ]);

      console.log('Subscriptions data:', subsResponse.data);
      console.log('Tenants data:', tenantsResponse.data);
      console.log('Plans data:', plansResponse.data);

      setSubscriptions(subsResponse.data || []);
      setTenants(tenantsResponse.data || []);
      setPlans(plansResponse.data || []);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      if (error.response) {
        console.error('Error response:', error.response.data);
        toast.error(`Failed to load data: ${error.response.data.message || error.message}`);
      } else {
        toast.error(`Failed to load data: ${error.message}`);
      }
      setLoading(false);

      // Set empty arrays to prevent UI errors
      setSubscriptions([]);
      setTenants([]);
      setPlans([]);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // For plan_id, calculate end_date based on billing cycle
    if (name === 'plan_id' && value) {
      const selectedPlan = plans.find(plan => plan.id === parseInt(value));
      if (selectedPlan) {
        // Calculate end date (1 month or 1 year from start date)
        const startDate = new Date(formData.start_date);
        let endDate = new Date(startDate);

        if (selectedPlan.billing_cycle === 'annual') {
          endDate.setFullYear(endDate.getFullYear() + 1);
        } else {
          endDate.setMonth(endDate.getMonth() + 1);
        }

        setFormData({
          ...formData,
          [name]: value,
          end_date: endDate.toISOString().split('T')[0]
        });
        return;
      }
    }

    setFormData({
      ...formData,
      [name]: value
    });
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.tenant_id) errors.tenant_id = 'Organization is required';
    if (!formData.plan_id) errors.plan_id = 'Subscription plan is required';
    if (!formData.start_date) errors.start_date = 'Start date is required';
    if (!formData.end_date) errors.end_date = 'End date is required';
    if (!formData.payment_method) errors.payment_method = 'Payment method is required';

    // Validate dates
    if (new Date(formData.end_date) <= new Date(formData.start_date)) {
      errors.end_date = 'End date must be after start date';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreateSubscription = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      // Use relative path in production, localhost in development
      const apiBaseURL = process.env.REACT_APP_API_URL ||
        (window.location.hostname === 'localhost' ? 'https://nivaas.app' : '');

      await axios.post('/api/saas/subscriptions', formData, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        ...(apiBaseURL && { baseURL: apiBaseURL })
      });

      toast.success('Subscription created successfully');
      setShowNewSubscriptionModal(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Error creating subscription:', error);
      toast.error(error.response?.data?.message || 'Failed to create subscription');
    }
  };

  const handleCancelSubscription = async (subscriptionId) => {
    if (!window.confirm('Are you sure you want to cancel this subscription?')) {
      return;
    }

    try {
      // Use relative path in production, localhost in development
      const apiBaseURL = process.env.REACT_APP_API_URL ||
        (window.location.hostname === 'localhost' ? 'https://nivaas.app' : '');

      await axios.post(`/api/saas/subscriptions/${subscriptionId}/cancel`, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        ...(apiBaseURL && { baseURL: apiBaseURL })
      });

      toast.success('Subscription cancelled successfully');
      fetchData();
    } catch (error) {
      console.error('Error cancelling subscription:', error);
      toast.error(error.response?.data?.message || 'Failed to cancel subscription');
    }
  };

  const resetForm = () => {
    setFormData({
      tenant_id: '',
      plan_id: '',
      start_date: new Date().toISOString().split('T')[0],
      end_date: '',
      payment_method: 'manual'
    });
    setFormErrors({});
  };

  // Helper function to get tenant name
  const getTenantName = (tenantId) => {
    const tenant = tenants.find(t => t.id === tenantId);
    return tenant ? tenant.name : 'Unknown';
  };

  // Helper function to get plan name
  const getPlanName = (planId) => {
    const plan = plans.find(p => p.id === planId);
    return plan ? plan.name : 'Unknown';
  };

  // Helper function to calculate subscription status
  const getSubscriptionStatus = (subscription) => {
    // First check the explicit status from database
    if (subscription.status) {
      const now = new Date();
      const endDate = new Date(subscription.end_date);

      switch (subscription.status.toLowerCase()) {
        case 'cancelled':
          return 'Cancelled';
        case 'pending':
          return 'Pending';
        case 'suspended':
          return 'Suspended';
        case 'inactive':
          return 'Inactive';
        case 'active':
          // For active status, still check if it's expired
          if (endDate < now) return 'Expired';
          return 'Active';
        default:
          break;
      }
    }

    // Fallback to legacy logic for backwards compatibility
    if (subscription.status === 'cancelled') return 'Cancelled';

    const now = new Date();
    const endDate = new Date(subscription.end_date);

    if (endDate < now) return 'Expired';
    if (subscription.payment_status === 'failed') return 'Payment Failed';
    return 'Active';
  };

  // Helper function to get status badge class
  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'Active': return 'bg-success';
      case 'Pending': return 'bg-warning text-dark';
      case 'Suspended': return 'bg-warning text-dark';
      case 'Inactive': return 'bg-secondary';
      case 'Expired': return 'bg-secondary';
      case 'Cancelled': return 'bg-danger';
      case 'Payment Failed': return 'bg-warning text-dark';
      default: return 'bg-secondary';
    }
  };

  // Filter subscriptions based on search and status filter
  const filteredSubscriptions = subscriptions.filter(subscription => {
    // Apply search filter (tenant name or plan name)
    const tenantName = getTenantName(subscription.tenant_id).toLowerCase();
    const planName = getPlanName(subscription.plan_id).toLowerCase();
    const matchesSearch =
      tenantName.includes(searchQuery.toLowerCase()) ||
      planName.includes(searchQuery.toLowerCase());

    // Apply status filter
    const status = getSubscriptionStatus(subscription);
    const matchesStatus =
      filterStatus === 'all' ||
      (filterStatus === 'active' && status === 'Active') ||
      (filterStatus === 'pending' && status === 'Pending') ||
      (filterStatus === 'suspended' && status === 'Suspended') ||
      (filterStatus === 'inactive' && status === 'Inactive') ||
      (filterStatus === 'expired' && status === 'Expired') ||
      (filterStatus === 'cancelled' && status === 'Cancelled') ||
      (filterStatus === 'failed' && status === 'Payment Failed');

    return matchesSearch && matchesStatus;
  });

  return (
    <div className="container-fluid mt-4">
      <PageTitle
        icon="fa-credit-card"
        title="Subscriptions"
        action={
          <button
            className="btn btn-primary"
            onClick={() => {
              resetForm();
              setShowNewSubscriptionModal(true);
            }}
          >
            <i className="fas fa-plus-circle me-2"></i>
            Add New Subscription
          </button>
        }
      />

      {/* Filters */}
      <div className="card shadow mb-4">
        <div className="py-3 superadmin-section">
          <h6 className="m-0 font-weight-bold">Filters</h6>
        </div>
        <div className="card-body">
          <div className="row">
            <div className="col-md-6 mb-3">
              <div className="input-group">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search by organization or plan..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                <button className="btn btn-outline-secondary searchFilterButton" type="button">
                  <i className="fas fa-search"></i>
                </button>
              </div>
            </div>
            <div className="col-md-6 mb-3">
              <select
                className="form-control"
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <option value="all">All Status</option>
                <option value="active">Active Only</option>
                <option value="pending">Pending Only</option>
                <option value="suspended">Suspended Only</option>
                <option value="inactive">Inactive Only</option>
                <option value="expired">Expired Only</option>
                <option value="cancelled">Cancelled Only</option>
                <option value="failed">Payment Failed</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Subscriptions Table */}
      <div className="card shadow mb-4">
        <div className="py-3 superadmin-section">
          <h6 className="m-0 font-weight-bold">Subscriptions</h6>
        </div>
        <div className="card-body">
          {loading ? (
            <div className="text-center my-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-bordered">
                <thead>
                  <tr>
                    <th>Organization</th>
                    <th>Plan</th>
                    <th>Start Date</th>
                    <th>End Date</th>
                    <th>Status</th>
                    <th>Payment</th>
                    <th>Amount</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSubscriptions.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="text-center">No subscriptions found</td>
                    </tr>
                  ) : (
                    filteredSubscriptions.map(subscription => {
                      const status = getSubscriptionStatus(subscription);
                      return (
                        <tr key={subscription.id}>
                          <td>{getTenantName(subscription.tenant_id)}</td>
                          <td>{getPlanName(subscription.plan_id)}</td>
                          <td>{new Date(subscription.start_date).toLocaleDateString()}</td>
                          <td>{new Date(subscription.end_date).toLocaleDateString()}</td>
                          <td>
                            <span className={`badge ${getStatusBadgeClass(status)}`}>
                              {status}
                            </span>
                          </td>
                          <td>
                            <div className="d-flex align-items-center" style={{ paddingBlockStart: '4px' }}>
                              <span className={`badge ${subscription.payment_status === 'paid' ? 'bg-success' :
                                subscription.payment_status === 'free' ? 'bg-info' : 'bg-warning text-dark'
                                }`}>
                                {subscription.payment_status || 'pending'}
                              </span>

                              {/* Add Record Payment button for pending payments */}
                              {(subscription.payment_status === 'pending' || !subscription.payment_status) && (
                                <a
                                  href={`/superadmin/subscriptions/${subscription.id}/transactions`}
                                  className="btn btn-sm btn-success ms-2"
                                  title="Record Payment"
                                >
                                  <i className="fas fa-money-bill-wave me-1"></i> Record
                                </a>
                              )}
                            </div>
                          </td>
                          <td>₹{parseFloat(subscription.price || 0).toFixed(2)}</td>
                          <td>
                            <div className="btn-group">
                              {subscription.status !== 'cancelled' && (
                                <button
                                  className="btn btn-sm btn-danger me-2"
                                  onClick={() => handleCancelSubscription(subscription.id)}
                                >
                                  <i className="fas fa-ban"></i>
                                </button>
                              )}
                              <a
                                href={`/superadmin/subscriptions/${subscription.id}/transactions`}
                                className="btn btn-sm btn-primary"
                                title="View Transaction History"
                              >
                                <i className="fas fa-history"></i>
                              </a>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* New Subscription Modal */}
      {showNewSubscriptionModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header superadmin-section">
                <h5 className="modal-title">Add New Subscription</h5>
                <button type="button" className="btn-close btn-close-white text-white" onClick={() => setShowNewSubscriptionModal(false)}></button>
              </div>
              <div className="modal-body">
                <form onSubmit={handleCreateSubscription}>
                  <div className="mb-3">
                    <label className="form-label">Organization</label>
                    <select
                      className={`form-control ${formErrors.tenant_id ? 'is-invalid' : ''}`}
                      name="tenant_id"
                      value={formData.tenant_id}
                      onChange={handleInputChange}
                    >
                      <option value="">Select Organization</option>
                      {tenants.map(tenant => (
                        <option key={tenant.id} value={tenant.id}>{tenant.name}</option>
                      ))}
                    </select>
                    {formErrors.tenant_id && <div className="invalid-feedback">{formErrors.tenant_id}</div>}
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Subscription Plan</label>
                    <select
                      className={`form-control ${formErrors.plan_id ? 'is-invalid' : ''}`}
                      name="plan_id"
                      value={formData.plan_id}
                      onChange={handleInputChange}
                    >
                      <option value="">Select Plan</option>
                      {plans.map(plan => (
                        <option key={plan.id} value={plan.id}>
                          {plan.name} - ₹{plan.price} / {plan.billing_cycle || 'month'}
                        </option>
                      ))}
                    </select>
                    {formErrors.plan_id && <div className="invalid-feedback">{formErrors.plan_id}</div>}
                  </div>
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Start Date</label>
                      <input
                        type="date"
                        className={`form-control ${formErrors.start_date ? 'is-invalid' : ''}`}
                        name="start_date"
                        value={formData.start_date}
                        onChange={handleInputChange}
                      />
                      {formErrors.start_date && <div className="invalid-feedback">{formErrors.start_date}</div>}
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">End Date</label>
                      <input
                        type="date"
                        className={`form-control ${formErrors.end_date ? 'is-invalid' : ''}`}
                        name="end_date"
                        value={formData.end_date}
                        onChange={handleInputChange}
                      />
                      {formErrors.end_date && <div className="invalid-feedback">{formErrors.end_date}</div>}
                    </div>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Payment Method</label>
                    <select
                      className={`form-control ${formErrors.payment_method ? 'is-invalid' : ''}`}
                      name="payment_method"
                      value={formData.payment_method}
                      onChange={handleInputChange}
                    >
                      <option value="manual">Manual Payment</option>
                      <option value="stripe">Stripe</option>
                      <option value="paypal">PayPal</option>
                    </select>
                    {formErrors.payment_method && <div className="invalid-feedback">{formErrors.payment_method}</div>}
                  </div>
                  <div className="modal-footer">
                    <button type="button" className="btn btn-secondary" onClick={() => setShowNewSubscriptionModal(false)}>Cancel</button>
                    <button type="submit" className="btn btn-primary">Create Subscription</button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Subscriptions; 